/**
 * @file ico-writer.ts
 * @description Windows Icon (ICO) format writer. Generates PNG-based ICO files.
 */

export class IcoWriter {
    /**
     * Builds an ICO binary package from provided images/layers.
     */
    async buildIco(doc: any): Promise<ArrayBuffer> {
        const layers = doc.layers || [];
        const images: { width: number; height: number; data: Uint8Array }[] = [];

        // If it's a multi-layer ICO import, we might have multiple sizes.
        // Otherwise, we take the composite at standard sizes.
        if (layers.length > 1 && layers.every((l: any) => l.name.startsWith("Icon"))) {
            for (const layer of layers) {
                const png = await this.canvasToPng(layer.canvas);
                images.push({
                    width: layer.canvas.width,
                    height: layer.canvas.height,
                    data: png
                });
            }
        } else {
            // Standard export: use composite at current size, plus common sizes if requested
            // For now, just the current size (clamped to 256)
            const png = await this.imageDataToPng(doc.composite || layers[0].canvas);
            images.push({
                width: Math.min(256, doc.width),
                height: Math.min(256, doc.height),
                data: png
            });
        }

        return this.serialize(images);
    }

    private serialize(images: { width: number; height: number; data: Uint8Array }[]): ArrayBuffer {
        const count = images.length;
        const headerSize = 6;
        const entrySize = 16;
        const directorySize = headerSize + count * entrySize;
        
        let totalDataSize = images.reduce((acc, img) => acc + img.data.length, 0);
        const buffer = new ArrayBuffer(directorySize + totalDataSize);
        const view = new DataView(buffer);
        const uint8 = new Uint8Array(buffer);

        // Header
        view.setUint16(0, 0, true);   // Reserved
        view.setUint16(2, 1, true);   // Type: ICO
        view.setUint16(4, count, true);

        let dataOffset = directorySize;

        // Entries
        for (let i = 0; i < count; i++) {
            const img = images[i];
            const entryPos = headerSize + i * entrySize;

            view.setUint8(entryPos, img.width >= 256 ? 0 : img.width);
            view.setUint8(entryPos + 1, img.height >= 256 ? 0 : img.height);
            view.setUint8(entryPos + 2, 0); // Palette
            view.setUint8(entryPos + 3, 0); // Reserved
            view.setUint16(entryPos + 4, 1, true);  // Color planes
            view.setUint16(entryPos + 6, 32, true); // BPP
            view.setUint32(entryPos + 8, img.data.length, true);
            view.setUint32(entryPos + 12, dataOffset, true);

            // Copy data
            uint8.set(img.data, dataOffset);
            dataOffset += img.data.length;
        }

        return buffer;
    }

    private async canvasToPng(canvas: HTMLCanvasElement | ImageData): Promise<Uint8Array> {
        if (canvas instanceof ImageData) {
            const tempCan = document.createElement("canvas");
            tempCan.width = canvas.width;
            tempCan.height = canvas.height;
            tempCan.getContext("2d")?.putImageData(canvas, 0, 0);
            return this.canvasToPng(tempCan);
        }
        
        return new Promise((resolve) => {
            canvas.toBlob((blob) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    resolve(new Uint8Array(reader.result as ArrayBuffer));
                };
                reader.readAsArrayBuffer(blob!);
            }, "image/png");
        });
    }

    private async imageDataToPng(data: ImageData): Promise<Uint8Array> {
        return this.canvasToPng(data);
    }
}
